import axios from "axios";

// Member categories, a company setting (slice 2): a two-level tree of
// categories and their subcategories. Contract: snb_lightspeed_backend
// docs/customer-module/kyc/13-Slice-2-Full-Replacement.md §2
// customer_categories, §3 Categories and §4 Settings.
//
// Every action resolves to the whole envelope (res.data), except
// fetchCategoryTree, which resolves to the array of categories. Rejections are
// what the global interceptor rejects with: the axios response. These are
// customer journey routes: a 422 carries its field errors under `data`, a 409
// (in use) its details under `data`.

export default {
    namespaced: true,

    state: {
        categoryTree: [],
        categoryPage: {},
        categories: [],
        category: {},
    },

    getters: {
        // [{customer_category_id, code, name, is_group, status_id, in_use,
        //   subcategories: [...]}] of the last tree read
        categoryTree(state) {
            return state.categoryTree;
        },
        // the envelope of the last settings page read: {data, pagination}
        categoryPage(state) {
            return state.categoryPage;
        },
        categories(state) {
            return state.categories;
        },
        category(state) {
            return state.category;
        },
    },

    mutations: {
        SET_CATEGORY_TREE(state, tree) {
            return (state.categoryTree = tree);
        },
        SET_CATEGORY_PAGE(state, envelope) {
            return (state.categoryPage = envelope);
        },
        SET_CATEGORIES(state, categories) {
            return (state.categories = categories);
        },
        SET_CATEGORY(state, category) {
            return (state.category = category);
        },
    },

    actions: {
        // GET /customer-categories?tree=1&report=1: the categories, each with
        // its subcategories[]. Pass is_group (0/1) for one type, status_id (1)
        // for the active rows only; omitted, every row is returned.
        async fetchCategoryTree({ commit }, params = {}) {
            const query = { ...(params || {}), tree: 1, report: 1 };
            Object.keys(query).forEach((key) => {
                if (query[key] === null || query[key] === undefined || query[key] === "") {
                    delete query[key];
                }
            });
            if (typeof query.is_group === "boolean") {
                query.is_group = query.is_group ? 1 : 0;
            }

            return await axios
                .get("/api/v1/customer-categories", { params: query })
                .then((res) => {
                    const tree = Array.isArray(res.data?.data) ? res.data.data : [];
                    commit("SET_CATEGORY_TREE", tree);
                    return tree;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // GET /customer-categories?tree=1&paginate=1: a page of categories
        // (parent_id null), each with all its subcategories[], for the
        // settings page. Params: page, per_page, q (the name or code of a
        // category or of one of its subcategories), is_group (0/1) and
        // status_id (1/99), which filter the categories only. Resolves to the
        // whole envelope, with `pagination`.
        async fetchCategoryPage({ commit }, params = {}) {
            const query = { ...(params || {}), tree: 1, paginate: 1 };
            Object.keys(query).forEach((key) => {
                if (query[key] === null || query[key] === undefined || query[key] === "") {
                    delete query[key];
                }
            });

            return await axios
                .get("/api/v1/customer-categories", { params: query })
                .then((res) => {
                    commit("SET_CATEGORY_PAGE", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // GET /customer-categories: a page of categories and subcategories
        // alike. Filters is_group, parent_id, status_id; report=1 unpaginated.
        async fetchCategories({ commit }, params) {
            return await axios
                .get("/api/v1/customer-categories", { params: params })
                .then((res) => {
                    commit("SET_CATEGORIES", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        async fetchCategory({ commit }, categoryId) {
            return await axios
                .get(`/api/v1/customer-categories/${categoryId}`)
                .then((res) => {
                    commit("SET_CATEGORY", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // parent_id makes a subcategory, which takes its parent's is_group
        async createCategory({ commit }, payload) {
            return await axios
                .post("/api/v1/customer-categories", payload)
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // PUT: keys left out keep their stored values (parent_id null turns a
        // subcategory into a category, so leave it out unless that is meant).
        // is_group and parent_id cannot change while the row is in use (409).
        async updateCategory({ commit }, { id, payload }) {
            return await axios
                .put(`/api/v1/customer-categories/${id}`, payload)
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // 409 when the row is in use
        async deleteCategory({ commit }, categoryId) {
            return await axios
                .delete(`/api/v1/customer-categories/${categoryId}`)
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },
    },
};
